import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "./cityPricing";

const bormesLesMimosas: ChauffeurPage = {
  slug: "bormes-les-mimosas",
  title: "Chauffeur privé & VTC à Bormes-les-Mimosas",
  subtitle:
    "Chauffeur privé à Bormes-les-Mimosas : village fleuri, Fort de Brégançon, port de La Favière et transferts aéroport Toulon-Hyères et Nice.",
  keywords: [
    "chauffeur privé bormes-les-mimosas",
    "vtc bormes",
    "transfert aéroport bormes",
    "chauffeur la favière",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Bormes-les-Mimosas",
    subtitle:
      "Du village perché fleuri au port de La Favière : un chauffeur pour le Cap Bénat et tout le littoral, 7j/7.",
    keywords: [
      "VTC Bormes-les-Mimosas",
      "chauffeur La Favière",
      "Fort de Brégançon",
      "transfert aéroport Toulon-Hyères",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Bormes-les-Mimosas",
    badges: [
      "Aéroport Toulon-Hyères ~35 min",
      "Village & littoral",
      "Chauffeurs FR/EN",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Bormes-les-Mimosas",
    items: [
      {
        title: "Village perché et bord de mer",
        description:
          "Ruelles en pente du vieux village et plages de La Favière : le chauffeur relie les deux sans contrainte de parking.",
      },
      {
        title: "Aéroport le plus proche",
        description:
          "Toulon-Hyères à une trentaine de minutes ; Nice Côte d'Azur reste accessible en près de 1h40.",
      },
      {
        title: "Cap Bénat & Brégançon",
        description:
          "Accès au Cap Bénat, à la plage de Cabasson et aux abords du Fort de Brégançon, résidence présidentielle.",
      },
      {
        title: "Service noté 5,0/5",
        description:
          "Chauffeurs discrets et ponctuels, basés sur la Côte d'Azur, notés 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Bormes",
    services: [
      {
        title: "Transfert aéroport de Toulon-Hyères",
        text: "Environ 30 à 35 min de trajet, avec suivi de vol, depuis et vers l'aéroport de Toulon-Hyères.",
      },
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison longue distance vers Nice Côte d'Azur en près de 1h40, en berline ou van premium.",
      },
      {
        title: "Village & port de La Favière",
        text: "Navettes entre le vieux village fleuri, le port de plaisance de La Favière et les plages.",
      },
      {
        title: "Gare TGV de Toulon",
        text: "Transfert vers la gare de Toulon pour les TGV vers Paris et les correspondances régionales.",
      },
      {
        title: "Mise à disposition & longue distance",
        text: "Chauffeur à la journée pour le Var, ou trajets vers Saint-Tropez, Cannes, Nice et l'Italie.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Vieux village de Bormes",
        description: "Village fleuri perché, ruelles et placettes.",
      },
      {
        name: "Port de La Favière",
        description: "Port de plaisance et plages du quartier maritime.",
      },
      {
        name: "Cap Bénat & Cabasson",
        description: "Domaine du Cap Bénat et plage de Cabasson.",
      },
      {
        name: "Fort de Brégançon",
        description: "Résidence présidentielle, à la pointe du littoral.",
      },
      {
        name: "Aéroport de Toulon-Hyères",
        description: "L'aéroport le plus proche, à environ 35 min.",
      },
      {
        name: "Le Lavandou",
        description: "Station voisine et ses douze plages.",
      },
    ],
  },
  generic: {
    heading: "Bormes-les-Mimosas, du village fleuri au Cap Bénat",
    subheading:
      "Un vieux village perché et un littoral discret, reliés par des routes étroites qu'un chauffeur connaît.",
    paragraphs: [
      "Bormes-les-Mimosas se vit sur deux niveaux : le vieux village fleuri, accroché à la colline avec ses ruelles en pente, et le bord de mer autour du port de La Favière. Entre les deux, le stationnement se complique dès le printemps, quand le mimosa puis l'été attirent les visiteurs. Un chauffeur privé fait la navette de porte à porte et vous dépose au plus près, en haut comme en bas.",
      "Le littoral de Bormes mène au Cap Bénat, à la plage de Cabasson et aux abords du Fort de Brégançon, résidence d'été des présidents de la République : des accès confidentiels que le chauffeur emprunte sans hésiter. Pour rejoindre la région, l'aéroport de Toulon-Hyères est le plus proche, à une trentaine de minutes ; Nice Côte d'Azur se gagne en près de 1h40, et la gare TGV de Toulon ouvre la ligne de Paris.",
      "En séjour, la mise à disposition attribue un chauffeur dédié à vos journées — village, plages, dîners voisins, escapades dans le golfe de Saint-Tropez — avec des forfaits fixes établis à l'avance et un devis immédiat.",
    ],
    bullets: [
      "Navette village ↔ littoral",
      "Accès Cap Bénat & Brégançon",
      "Forfaits fixes & devis immédiat",
    ],
  },
  process: {
    heading: "Réserver un chauffeur à Bormes",
    steps: [
      "Indiquez votre vol, votre train ou votre adresse (village ou La Favière).",
      "Nous conseillons le véhicule selon le groupe, les bagages et le trajet.",
      "Le chauffeur suit le vol et adapte le départ au trafic du littoral.",
      "Transfert direct ou mise à disposition pour le village et les plages.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Bormes-les-Mimosas"),
    {
      q: "Quel aéroport est le plus proche de Bormes ?",
      a: "L'aéroport de Toulon-Hyères, à une trentaine de minutes, est le plus proche. Nice Côte d'Azur, pour les vols internationaux, se trouve à près de 1h40 par l'autoroute.",
    },
    {
      q: "Desservez-vous le vieux village perché malgré les ruelles ?",
      a: "Oui. Le cœur du village est en partie piéton ; nous vous déposons au plus près des accès et reprenons au même point, sans que vous ayez à chercher une place en pente.",
    },
    {
      q: "Assurez-vous les transferts vers la gare TGV de Toulon ?",
      a: "Oui, la gare de Toulon relie Paris en TGV et assure les correspondances ; nous calons le départ sur votre horaire, avec une marge pour la circulation côtière.",
    },
    {
      q: "Combien de temps à l'avance faut-il réserver ?",
      a: "Anticipez pendant la floraison du mimosa en hiver et en pleine saison estivale ; les demandes de dernière minute restent possibles selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais et habitués aux visiteurs internationaux du village et du littoral.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes Classe E ou Classe S, van premium 7 places pour les groupes et les bagages, SUV et minibus VIP selon vos besoins.",
    },
    {
      q: "Faites-vous les longues distances depuis Bormes ?",
      a: "Oui : Saint-Tropez, Cannes, Nice, Monaco, Marseille et l'Italie, ainsi que la desserte depuis Cannes ou Nice vers Bormes, en véhicule confortable et chauffeur dédié.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur privé Saint-Tropez",
      href: "/chauffeur/saint-tropez",
    },
    {
      label: "Transferts sur la Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Mise à disposition avec chauffeur à Cannes",
      href: "/chauffeur/cannes",
    },
    { label: "Chauffeur privé à Nice", href: "/chauffeur/nice" },
  ],
  cta: {
    title: "Votre chauffeur privé à Bormes-les-Mimosas",
    text: "Transfert aéroport, village fleuri ou port de La Favière : réservez votre chauffeur, réponse rapide 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Bormes — VTC & transfert aéroport",
    description:
      "Chauffeur privé à Bormes-les-Mimosas : transfert aéroport de Toulon-Hyères (~35 min) et de Nice, gare TGV de Toulon, Cap Bénat et La Favière.",
    canonical: "/chauffeur/bormes-les-mimosas",
  },
};

export default bormesLesMimosas;
